/// <reference types="wicg-file-system-access" />

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { NotificationsActions } from 'src/app/state/notifications/notifications.actions';
import { Severity } from 'src/app/global/enums/severity';
import { catchError, EMPTY, tap } from 'rxjs';
import { PredictorModelActions } from '../../state/predictor-model/predictor-model.actions';
import { RequestPayload } from '../interfaces/simulation-export-type';

@Injectable({
  providedIn: 'root',
})

export class ExportService {
  private readonly http: HttpClient;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly store: Store
  ) {
    this.http = this.httpClient;
  }

  public exportTIFF(simulationId: string) {
    const endpoint: string = 'api/Export/ExportTIFF';
    const queryParameter: string = '?simulationId=' + simulationId;
    const url: string = endpoint + queryParameter;
    return this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        tap(() =>
          this.store.dispatch(new PredictorModelActions.SetExporting(false))
        )
      )
      .subscribe((blob) => {
        // Show Save As dialog so user can choose a file name
        this.downloadFile(blob, 'DebrisFlowPredictorExport.tiff');
      });
  }

  public exportLayer(simulationId: string) {
    const endpoint: string = 'api/Export/ExportLayer';
    const queryParameter: string = '?simulationId=' + simulationId;
    const url: string = endpoint + queryParameter;
    return this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        tap(() =>
          this.store.dispatch(new PredictorModelActions.SetExporting(false))
        )
      )
      .subscribe((blob) => {
        this.downloadFile(blob, 'ExportLayer.zip');
      });
  }

  public exportSimulationResultFiles(simulationId: string, simulationName?: string, scenario?: string) {
    const url: string = `api/Export/simulation/${simulationId}`;

    const fileName = simulationName ? `${simulationName}.zip` : 'SimulationResults.zip'

    return this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        tap(() =>
          this.store.dispatch(new PredictorModelActions.SetExporting(false))
        )
      )
      .subscribe({
        next: (blob) => {
          this.downloadFile(blob, fileName);
        },
        error: () => {
          this.store.dispatch(
            new NotificationsActions.Toast(
              'An error occurred while exporting the simulation results.',
              Severity.Error
            )
          );
        },
      });
  }

  public exportMultipleSimulationsZip(payload: string[]) {
    const url = '/api/Export/ExportMultipleSimulations';
    this.store.dispatch(new NotificationsActions.Toast(
      'Export in progress. This may take a moment depending on file size.',
      Severity.Success
    ));

    this.store.dispatch(new PredictorModelActions.SetExporting(true));

    this.http.post(url, payload, { responseType: 'blob' }).subscribe({
      next: async (blob) => {
        const zipFileName = `SimulationResults_${new Date().getTime()}.zip`;
        await this.downloadFile(blob, zipFileName);
      },
      error: () => {
        this.store.dispatch(
          new NotificationsActions.Toast(
            'An error occurred while exporting the simulation results.',
            Severity.Error
          )
        );
      },
    });
  }


  public exportSimulationResult(simulationId: string, resultType: string) {
    const url: string = `api/Export/simulation/${simulationId}/${resultType}`;

    this.http
      .get(url, { responseType: 'blob', observe: 'response' })
      .pipe(
        tap(() =>
          this.store.dispatch(new PredictorModelActions.SetExporting(false))
        ),
        catchError((error) => {
          this.store.dispatch(new PredictorModelActions.SetExporting(false))
          this.store.dispatch(
            new NotificationsActions.Toast(
              'The simulation data results does not exists for simulation: ' + simulationId,
              Severity.Error
            )
          );
          return EMPTY;
        })
      )
      .subscribe((response) => {
        const blob: Blob = response.body!;
        const contentDisposition: string | null = response.headers.get(
          'Content-Disposition'
        );

        // Extract filename from Content-Disposition header
        let filename: string = 'invalid';
        if (contentDisposition) {
          const filenameMatch: RegExpMatchArray | null =
            contentDisposition.match(
              /filename\*=(?:UTF-8'')?([^;]+)|filename="?([^";]+)"?/
            );

          if (filenameMatch) {
            filename = filenameMatch[1] || filenameMatch[2] || filename;
          }
        }

        this.downloadFile(blob, filename);
      });
  }

  async downloadFile(blob: Blob, filename: string) {

    if (!('showSaveFilePicker' in window)) {
    this.legacyDownload(blob, filename);
    return;
  }

  const handle = await (window as any).showSaveFilePicker({
    suggestedName: filename
  });

  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
  }

  async legacyDownload(blob: Blob, filename: string) {
     const objectUrl: string = URL.createObjectURL(blob);
  const link: HTMLAnchorElement = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(objectUrl);
  }

  public exportLandslideSummary(simulationId: string) {
    const endpoint: string = '/api/Export/ExportLandslideSummary';
    const queryParameter: string = '?simulationId=' + simulationId;
    const url: string = endpoint + queryParameter;
    return this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        tap(() =>
          this.store.dispatch(new PredictorModelActions.SetExporting(false))
        )
      )
      .subscribe((blob) => {
        this.downloadFile(blob, 'LandslideSummary.xlsx');
      });
  }

  public exportSimulationData(
    simulationId: string,
    getDataForInspectedCell: boolean | undefined,
    cellIndex: number | undefined
  ) {
    const endpoint: string = '/api/Export/ExportLatestSimulationData';
    const queryParameter: string =
      '?simulationId=' +
      simulationId +
      (getDataForInspectedCell && cellIndex !== undefined
        ? '&inspectedCellIndex=' + cellIndex
        : '');
    const url: string = endpoint + queryParameter;
    return this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        tap(() =>
          this.store.dispatch(new PredictorModelActions.SetExporting(false))
        )
      )
      .subscribe((blob) => {
        this.downloadFile(blob, 'SimulationData.csv');
      });
  }

  public exportLandslidesToPoints(simulationId: string) {
    const url: string = `/api/Export/ExportLandslidesToPoints/${simulationId}`;

    return this.http
      .get(url, { responseType: 'blob' })
      .pipe(
        tap(() =>
          this.store.dispatch(new PredictorModelActions.SetExporting(false))
        )
      )
      .subscribe((blob) => {
        this.downloadFile(blob, 'LandslidesToPoints.zip');
      });
  }

  public exportSelectedSimulationByTypes(payload: RequestPayload) {
    const url: string = '/api/Export/ExportSimulationMultipleFileTypes';
    this.store.dispatch(new NotificationsActions.Toast(
      'Export in progress. This may take a moment depending on file size.',
      Severity.Success
    ));

    return this.http.post(url, payload, { responseType: 'blob' })
      .pipe(tap(() =>
        this.store.dispatch(new PredictorModelActions.SetExporting(true))
      ))
  }

  async downloadZipFile(blob: Blob, filename: string) {
    const zipBlob = new Blob([blob], { type: 'application/zip' });

    if ('showSaveFilePicker' in window) {
      try {
        const handle: any = await (window as any).showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: 'ZIP archive',
            accept: { 'application/zip': ['.zip'] }
          }]
        });
        const writable: any = await handle.createWritable();
        await writable.write(zipBlob);
        await writable.close();

        this.store.dispatch(new NotificationsActions.Toast(
          'Export completed successfully.',
          Severity.Success
        ));
      } catch (error: any) {
        console.error(error);
        this.store.dispatch(new NotificationsActions.Toast(
          'An error occurred while exporting the simulation results.',
          Severity.Error
        ));

        // Fallback legacy download
        const link = document.createElement('a');
        link.href = URL.createObjectURL(zipBlob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      }
    }
  }
}
